import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const fileStorageTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isRails = stack.backend === 'rails';
  const isPython = stack.backend === 'python-fastapi';
  const isNode =
    stack.backend === 'node-express' || stack.backend === 'node-fastify';

  const backend =
    (featureConfig?.options['storage-backend'] as string | undefined) ?? 's3';
  const isS3 = backend === 's3';
  const isLocal = backend === 'local';

  const hasVirusScan =
    (featureConfig?.options['virus-scan'] as string | undefined) === 'enabled';

  // Stack-specific upload library note
  const uploadLib = isRails
    ? 'Active Storage'
    : isPython
      ? 'boto3 (AWS SDK for Python)'
      : 'AWS SDK v3 (@aws-sdk/client-s3 + @aws-sdk/s3-request-presigner)';

  return {
    pages: [
      {
        id: 'file-manager',
        path: '/files',
        label: 'File manager',
        description:
          'Grid/list view of uploaded files with search, sort, and bulk delete',
        authRequired: true,
      },
      {
        id: 'file-preview',
        path: '/files/:fileId',
        label: 'File preview',
        description:
          'Preview images and PDFs inline; show metadata and download link for other types',
        authRequired: true,
      },
    ],
    uxFlows: [
      {
        id: 'upload-file',
        title: 'User uploads a file (presigned URL)',
        steps: isRails
          ? [
              'User selects a file via drag-and-drop or file picker',
              'Client sends multipart POST /api/files/upload to Rails backend',
              'Active Storage receives the upload, stores to configured service (local disk in dev, S3 in prod)',
              'Active Storage generates a blob record and attaches it to the model',
              'Server returns file metadata including the variant/attachment URL',
            ]
          : isLocal
            ? [
                'User selects a file via drag-and-drop or file picker',
                `Client sends multipart POST /api/files/upload (${isNode ? 'multer/busboy streams to disk' : 'python-multipart + aiofiles streams to disk'})`,
                'Server validates mime type and size, saves to /uploads/<uuid>/<filename>',
                'Server records file metadata in DB',
                'File served via GET /api/files/:id with auth check',
              ]
            : [
                'User selects a file via drag-and-drop or file picker',
                'Client calls POST /api/files/upload-url with filename, contentType, and size',
                `Server validates size limit, generates presigned S3 PUT URL using ${uploadLib} (15-minute expiry)`,
                'Client uploads file directly to S3 using the presigned URL — bypasses server, reduces bandwidth cost',
                'Client calls POST /api/files to record metadata (storage_path, size_bytes, etc.) in DB',
                'Server optionally queues a virus scan job after confirmation',
              ],
      },
      {
        id: 'download-file',
        title: 'User downloads or views a file',
        steps: isRails
          ? [
              'User clicks download or preview link',
              'Rails redirects to a short-lived signed URL via active_storage_blob_url or redirect_to attachment.url',
              'Browser downloads file directly from storage',
            ]
          : isLocal
            ? [
                'User clicks download link — browser requests GET /api/files/:id',
                'Server verifies user has access to the file',
                'Server streams file from disk with correct Content-Type header',
              ]
            : [
                'User clicks download or preview link',
                'Client calls GET /api/files/:id',
                'Server verifies ownership/permissions, generates a presigned S3 GET URL (5-minute expiry)',
                'Server redirects or returns { downloadUrl } to client',
                'Browser fetches file directly from S3',
              ],
      },
      {
        id: 'delete-file',
        title: 'User deletes a file',
        steps: [
          'User clicks delete in file manager — confirmation dialog appears',
          'Client calls DELETE /api/files/:id',
          isRails
            ? 'Rails Active Storage purges the blob and removes the attachment from S3/disk'
            : isLocal
              ? 'Server removes the file from disk and deletes the DB record'
              : 'Server calls S3 DeleteObject, sets deleted_at in DB (soft delete)',
          'File disappears from /files list immediately via optimistic update',
        ],
      },
      ...(hasVirusScan
        ? [
            {
              id: 'virus-scan',
              title: 'Uploaded file is scanned for malware',
              steps: [
                'After file is confirmed uploaded, server enqueues a scan job',
                'Worker downloads file from S3 and submits to ClamAV or VirusTotal API',
                'If clean: PATCH /api/files/:id/status sets status to "ready"',
                'If infected: file is deleted from S3, DB record marked "quarantined", user notified',
                'Files with status "pending" show a scanning indicator in the UI',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      ...(isRails
        ? [
            {
              id: 'upload',
              method: 'POST' as const,
              path: '/api/files/upload',
              description:
                'Multipart upload handled by Active Storage — attaches blob to model',
              requestBody: 'multipart/form-data: file',
              response:
                '{ file: { id, url, filename, contentType, byteSize } }',
            },
          ]
        : [
            {
              id: 'presign',
              method: 'POST' as const,
              path: '/api/files/upload-url',
              description: isS3
                ? `Generate a presigned S3 PUT URL for direct browser upload using ${uploadLib}`
                : 'Generate a signed upload URL for cloud storage',
              requestBody: '{ filename, contentType, sizeBytes }',
              response: '{ uploadUrl, fileId, expiresAt }',
            },
            {
              id: 'confirm',
              method: 'POST' as const,
              path: '/api/files',
              description:
                'Record file metadata after client confirms successful upload',
              requestBody:
                '{ fileId, name, mimeType, sizeBytes, storagePath, isPublic? }',
              response: '{ file }',
            },
          ]),
      {
        id: 'get-file',
        method: 'GET',
        path: '/api/files/:id',
        description: isLocal
          ? 'Serve file from local disk with auth check and correct Content-Type'
          : 'Return file metadata and a short-lived signed download URL',
        response: isLocal
          ? 'File stream (Content-Disposition: attachment)'
          : '{ file, downloadUrl }',
      },
      {
        id: 'list-files',
        method: 'GET',
        path: '/api/files',
        description:
          'List files for the current user with pagination and optional search',
        response: '{ files: File[], total: number, nextCursor?: string }',
      },
      {
        id: 'delete-file',
        method: 'DELETE',
        path: '/api/files/:id',
        description: isLocal
          ? 'Delete file from disk and remove DB record'
          : 'Soft-delete DB record and delete object from S3',
      },
    ],
    dbTables: isMongo
      ? [
          {
            id: 'files',
            name: 'files',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              { name: 'name', type: 'string', nullable: false },
              { name: 'mimeType', type: 'string', nullable: false },
              {
                name: 'sizeBytes',
                type: 'number',
                nullable: false,
                notes: 'File size in bytes',
              },
              {
                name: 'storagePath',
                type: 'string',
                nullable: false,
                notes: 'S3 key or local relative path',
              },
              {
                name: 'storageProvider',
                type: 'string',
                nullable: false,
                notes: 's3 | local | gcs',
              },
              { name: 'isPublic', type: 'boolean', nullable: false },
              {
                name: 'status',
                type: 'string',
                nullable: false,
                notes: 'pending | ready | quarantined',
              },
              { name: 'deletedAt', type: 'Date', nullable: true },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
            notes:
              'Index userId + createdAt. Filter out deletedAt IS NOT NULL in queries.',
          },
        ]
      : [
          {
            id: 'files',
            name: 'files',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              { name: 'name', type: 'text', nullable: false },
              { name: 'mime_type', type: 'text', nullable: false },
              {
                name: 'size_bytes',
                type: 'bigint',
                nullable: false,
                notes: 'Use bigint — files can exceed 2 GB',
              },
              {
                name: 'storage_path',
                type: 'text',
                nullable: false,
                notes: 'S3 object key or local relative path from upload root',
              },
              {
                name: 'storage_provider',
                type: 'text',
                nullable: false,
                notes: 's3 | local | gcs — allows multi-provider setups',
              },
              { name: 'is_public', type: 'boolean', nullable: false },
              {
                name: 'status',
                type: 'text',
                nullable: false,
                notes:
                  'pending | ready | quarantined — set to pending until virus scan completes',
              },
              {
                name: 'deleted_at',
                type: 'timestamp',
                nullable: true,
                notes:
                  'Soft delete — S3 object also deleted, but record retained for audit',
              },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes:
              'Index (user_id, created_at DESC) for file list queries. Exclude deleted_at IS NOT NULL in all user-facing queries.',
          },
        ],
  };
};
